import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import SmartCombobox from "@/components/SmartCombobox";
import TestingDetailsChecklist from "@/components/TestingDetailsChecklist";
import ManageOptionsDialog from "@/components/ManageOptionsDialog";

const STATUS_OPTS = [
  { v: "Pass", color: "border-[#22C55E]/60 bg-[#DCFCE7] text-[#166534]", ring: "ring-[#22C55E]" },
  { v: "Failed", color: "border-[#EF4444]/60 bg-[#FEE2E2] text-[#991B1B]", ring: "ring-[#EF4444]" },
  { v: "Pending", color: "border-[#F59E0B]/60 bg-[#FEF3C7] text-[#92400E]", ring: "ring-[#F59E0B]" },
];

export default function InventoryFormDialog({ open, onOpenChange, editing, form, setForm, saving, onSave, previewAssetId, categories }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] xl:max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="inventory-sheet">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {editing ? `Edit ${editing.asset_id}` : "New testing entry"}
          </DialogTitle>
          <DialogDescription>
            {editing ? "Update device details and testing status." : "Fill device details. Asset ID auto-generates from the batch code."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Batch Code</Label></div>
              <Input
                value={form.batch_code}
                onChange={(e) => setForm({ ...form, batch_code: e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase() })}
                placeholder="AA / AB / Z"
                disabled={!!editing}
                data-testid="form-batch-code"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Asset ID</Label></div>
              <Input
                value={editing ? editing.asset_id : previewAssetId}
                readOnly
                disabled
                placeholder="auto-generated"
                className="font-mono"
                data-testid="form-asset-id"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between h-7">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Model</Label>
                <ManageOptionsDialog optionType="models" label="Models" testId="manage-models" />
              </div>
              <SmartCombobox
                value={form.model_name}
                onChange={(v) => setForm({ ...form, model_name: v })}
                optionType="models"
                placeholder="Latitude 7490 / ThinkPad T480…"
                testId="form-model"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Serial Number</Label></div>
              <Input
                value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                placeholder="e.g. SN12345XYZ"
                className="font-mono"
                data-testid="form-serial"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between h-7">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Configuration</Label>
                <ManageOptionsDialog optionType="configurations" label="Configurations" testId="manage-configurations" />
              </div>
              <SmartCombobox
                value={form.configuration}
                onChange={(v) => setForm({ ...form, configuration: v })}
                optionType="configurations"
                placeholder='i5-6500 / 8GB / 256GB SSD / 14"'
                testId="form-config"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between h-7">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Device Category</Label>
                <ManageOptionsDialog optionType="device_categories" label="Categories" testId="manage-device-categories" />
              </div>
              <RadioGroup
                value={form.device_category}
                onValueChange={(v) => setForm({ ...form, device_category: v })}
                className="flex flex-wrap gap-2"
                data-testid="form-device-category"
              >
                {categories.map((c) => {
                  const selected = form.device_category === c;
                  return (
                    <label
                      key={c}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-all ${
                        selected ? "border-[#0055FF] bg-[#EFF4FF] text-[#0055FF] font-medium" : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#94A3B8]"
                      }`}
                      data-testid={`form-device-category-${c}`}
                    >
                      <RadioGroupItem value={c} className="border-current data-[state=checked]:bg-current" />
                      <span className="select-none">{c}</span>
                    </label>
                  );
                })}
                {categories.length === 0 && <span className="text-xs text-[#64748B]">No categories yet — add one via "Categories".</span>}
              </RadioGroup>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between h-7">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Testing Details</Label>
              <ManageOptionsDialog optionType="testing_details" label="Testing Details" testId="manage-testing-details" />
            </div>
            <TestingDetailsChecklist
              values={form.testing_details}
              onChange={(v) => setForm({ ...form, testing_details: v })}
              testId="form-testing-details"
            />
            <p className="text-[11px] text-[#64748B]">Tick what passed inspection. Use "Add custom note" for anything outside the checklist.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Testing Status</Label>
            <RadioGroup
              value={form.testing_status}
              onValueChange={(v) => setForm({ ...form, testing_status: v })}
              className="grid grid-cols-3 gap-2"
              data-testid="form-testing-status"
            >
              {STATUS_OPTS.map((opt) => {
                const selected = form.testing_status === opt.v;
                return (
                  <label
                    key={opt.v}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-md border cursor-pointer text-sm transition-all ${
                      selected ? `${opt.color} ring-2 ${opt.ring}/30 font-medium` : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#94A3B8]"
                    }`}
                    data-testid={`form-testing-status-${opt.v}`}
                  >
                    <RadioGroupItem value={opt.v} className="border-current data-[state=checked]:bg-current" />
                    <span className="select-none">{opt.v}</span>
                  </label>
                );
              })}
            </RadioGroup>
            <p className="text-[11px] text-[#64748B]">Only "Pass" items become available for sale.</p>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Create entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
